import React, { useEffect } from 'react';
import FormInput from '../common/FormInput';
import {
    Locations,
    PageNames,
    ProductTypes,
    RoutePaths
} from '../../Constants/Common';
import { NavLink, useLocation } from 'react-router-dom';
import ProgressBar, {
    ProgressBarItem
} from '../common/progressBar/ProgressBar';
import { CreatedRepairRequest } from '../common/CommonTypes';
import { GoogleAnalytics } from '../../classes/GoogleAnalytics';
import { useAlloyContent } from '../hooks/useAlloyContent';
import { useCosmeticContent } from '../hooks/useCosmeticContent';
import PageTitle from '../common/PageTitle';
import ReloadHandler from '../common/ReloadHandler';
import { useSelectedVehicle } from '../hooks/useSelectedVehicle';
import i18next, { t } from 'i18next';
import { TranslationHelper } from 'classes/TranslationHelper';

export type ConfirmationLocationState = {
    progressBarItems?: ProgressBarItem[];
    repairRequests: CreatedRepairRequest[];
    productType: string;
};

const ConfirmationPage = (): JSX.Element => {
    useSelectedVehicle(); //this hook redirects back to the landing page if the vehicle is not in the context (i.e. page refresh)

    const location = useLocation();
    const { progressBarItems, repairRequests, productType } =
        location.state as ConfirmationLocationState;
    const getConfirmationText =
        productType === ProductTypes.Alloy ||
        productType === ProductTypes.AlloyRepair
            ? useAlloyContent()
            : useCosmeticContent();

    useEffect(() => {
        const pageName =
            productType === ProductTypes.Alloy ||
            productType === ProductTypes.AlloyRepair
                ? PageNames.AlloyConfirmation
                : PageNames.CosmeticConfirmation;
        GoogleAnalytics.setPageView(pageName);
    }, []);

    const translateProductType = (productType: string): string => {
        let translatedString = '';
        const textArray = productType.split(/([,-])/);
        textArray.forEach((ta) => {
            const trimmedText = ta.trim();
            const translationKey = trimmedText
                .toLowerCase()
                .replaceAll(' ', '_');
            const translatedText = t(translationKey, trimmedText);
            translatedString = translatedString.concat(
                ta.replace(trimmedText, translatedText)
            );
        });

        return translatedString;
    };

    return (
        <>
            <ProgressBar
                id='alloyProgressBar'
                progressBarItems={progressBarItems}
            />

            <div className='card page-content'>
                {(!progressBarItems || progressBarItems.length === 0) && (
                    <PageTitle title={t('confirmation')} />
                )}

                <div className='p-3 m-0'>
                    <p>{t('alloy_repair_request.step_4_confirmation_text')}</p>{' '}
                    <p>{getConfirmationText}</p>
                </div>
                <div className='card-body page-content-body'>
                    <FormInput
                        id='repair-request-forminput'
                        label={t('repair_requests')}>
                        {repairRequests &&
                            repairRequests.map((request, index) => (
                                <div
                                    id={`repair-request-${index.toString()}`}
                                    className='submitted-repair-request'
                                    key={index}>
                                    <b>{t('product_reference')}:</b>
                                    <span>{request.productReference}</span>
                                    <br />
                                    <b>{t('product_type')}:</b>
                                    <span>
                                        {translateProductType(
                                            request.productType
                                        )}
                                    </span>
                                    <br />
                                    <b>{t('repair_reference')}:</b>
                                    <span>{request.claimCode}</span>
                                    <br />
                                    <b>{t('repair_request_date')}:</b>
                                    <span>{request.submittedDate}</span>
                                    <br />
                                    <b>{t('status')}:</b>
                                    <span>
                                        {TranslationHelper.getTranslatedText(
                                            request.status
                                        )}
                                    </span>
                                </div>
                            ))}
                    </FormInput>
                </div>
            </div>
            <div className='bottom-navigation'>
                <NavLink
                    to={RoutePaths.Home}
                    className='btn btn-primary progress-button'>
                    {i18next.language !== Locations.Us
                        ? t('your_details')
                        : t('vehicles')}
                </NavLink>
            </div>
            <ReloadHandler reloadPath={RoutePaths.LandingPage} />
        </>
    );
};

export default ConfirmationPage;
